import { RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DragPreviewProps, NodeApi, NodeRendererProps, Tree, TreeApi } from 'react-arborist';
import useResizeObserver from 'use-resize-observer';

import { CreateStopsManuallyDialog } from '../../../../../../dialogs/create-stops-manually/CreateStopsManuallyDialog';
import { DataImporter } from '../../../../../../dialogs/stops-data-importer';
import { useRetrieveActiveProject } from '../../../../../../hooks/project/useRetrieveActiveProject';
import { ButtonProps } from '../../../../../../react-components/Button';
import { INTERCOM_EVENTS, intercom } from '../../../../../../services/intercom';
import { useUpdateDriverStops } from '../../../hooks/driver/useUpdateDriverStops';
import { useRetrieveActivePlan } from '../../../hooks/plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../hooks/plan-details/useRetrieveActivePlanStops';
import { PlanOverviewStopChip } from '../plan-overview-stop-chip/PlanOverviewStopChip';
import { PlanOverviewListLoading } from './PlanOverviewListLoading';
import { PlanOverviewListContainer, TREE_ITEM_HEIGHT } from './PlanOverviewListStyles';
import { DriverElement } from './drivers/DriverElement';
import { DriverNoStopsElement } from './drivers/DriverNoStopsElement';
import { StopElement } from './stops/StopElement';
import { UnassignedElement } from './unassigned/UnassignedElement';
import { UnassignedNoStopsElement } from './unassigned/UnassignedNoStopsElement';

type TreeApiDataElementsType =
  | `stop`
  | `driver`
  | `driver-no-stops`
  | `unassigned`
  | `unassigned-no-stops`
  | `plan-no-stops`;

interface TreeApiDataBase {
  id: string;
  type: TreeApiDataElementsType;
  isFirstInLevel?: boolean;
  isLastInLevel?: boolean;
}

export interface TreeApiData extends TreeApiDataBase {
  children: TreeApiDataBase[];
}

interface PlanOverviewListProps {
  selectedStops: string[];
  onSelectStops: (stopIds: string[]) => void;
}

export const PlanOverviewList = ({ selectedStops, onSelectStops }: PlanOverviewListProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: plan } = useRetrieveActivePlan();
  const { data: stops, isLoading: isStopsLoading, map: stopsMap } = useRetrieveActivePlanStops();
  const { data: drivers, isLoading: isDriversLoading, map: driversMap } = useRetrieveActivePlanDrivers();
  const { run: updateDriverStops } = useUpdateDriverStops();
  const { ref, width, height } = useResizeObserver();

  const treeRef = useRef<TreeApi<TreeApiData>>(null);

  useEffect(() => {
    if (!treeRef.current) return;

    // Setting the tree node selection directly to the state lets us avoid the onSelect event
    // and gives us more control over this interaction.
    treeRef.current.state.nodes.selection.ids = new Set(selectedStops);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStops]);

  const [dialog, setDialog] = useState<`createStopsManually` | `stopsFromFile` | undefined>(undefined);

  const hideCompleted = activeProject?.plans.hide_completed_stops;

  const visibleStops = useMemo(() => {
    return stops?.filter((stop) => !hideCompleted || stop.status === `pending`) ?? [];
  }, [hideCompleted, stops]);

  const overviewListData: {
    data: TreeApiData[];
    openMap: Record<string, boolean>;
  } = useMemo(() => {
    const driversOpenMap: Record<string, boolean> = {};

    const driversElements = (drivers ?? []).map((driver, index, array) => {
      const driverStops = visibleStops
        .filter((stop) => stop.driver_id === driver.id)
        .sort((a, b) => a.order! - b.order!);

      const children =
        driverStops.length > 0
          ? driverStops.map((stop, index, array) => ({
              id: stop.id,
              type: `stop` as TreeApiDataElementsType,
              isFirstInLevel: index === 0,
              isLastInLevel: index === array.length - 1,
            }))
          : [
              {
                id: `${driver.id}-no-stops`,
                type: `driver-no-stops` as TreeApiDataElementsType,
              },
            ];

      // TODO: DECIDIR SI DEIXAR OBERT PER DEFECTE EL DRIVER SI TE STOPS O NO
      // driversOpenMap[driver.id] = driverStops.length > 0;

      return {
        id: driver.id,
        type: `driver` as TreeApiDataElementsType,
        isFirstInLevel: index === 0,
        isLastInLevel: index === array.length - 1,
        children,
      };
    });

    const hasStops = (stops ?? []).length > 0;
    const unassignedStops = visibleStops.filter((stop) => !stop.driver_id);

    let unassignedChildren: TreeApiDataBase[] = [];

    if (!hasStops) {
      unassignedChildren = [
        {
          id: `plan-no-stops`,
          type: `plan-no-stops` as TreeApiDataElementsType,
        },
      ];
    } else {
      unassignedChildren = [
        ...unassignedStops.map((stop, index, array) => ({
          id: stop.id,
          type: `stop` as TreeApiDataElementsType,
          isFirstInLevel: index === 0,
          isLastInLevel: index === array.length - 1,
        })),
        {
          id: `unassigned-no-stops`,
          type: `unassigned-no-stops` as TreeApiDataElementsType,
        },
      ];
    }

    return {
      data: [
        ...driversElements,
        {
          id: `unassigned`,
          type: `unassigned`,
          children: unassignedChildren,
        },
      ],
      openMap: {
        ...driversOpenMap,
        [`unassigned`]: !hasStops || !visibleStops.some((stop) => stop.driver_id),
      },
    };
  }, [drivers, stops, visibleStops]);

  if (!plan || isDriversLoading || isStopsLoading) {
    return <PlanOverviewListLoading />;
  }

  return (
    <>
      <PlanOverviewListContainer wrap="nowrap" container direction="column" ref={ref}>
        <Tree
          ref={treeRef}
          paddingBottom={55}
          data={overviewListData.data}
          openByDefault={false}
          height={height}
          width={width}
          indent={12}
          rowHeight={TREE_ITEM_HEIGHT}
          initialOpenState={overviewListData.openMap}
          disableEdit
          disableDrop={({
            parentNode,
            dragNodes,
            index,
          }: {
            parentNode: NodeApi<TreeApiData>;
            dragNodes: NodeApi<TreeApiData>[];
            index: number;
          }) => {
            // TODO: CONTROL LOCKED DRIVERS ( This seems to work)

            // If container is Unassigned and it's empty, only allow drag at the first position
            // if (parentNode.id === `unassigned` && (parentNode.children ?? []).length > 0) return index > 0;
            // Drag above the list of drivers is not allowed
            return parentNode.level < 0;
          }}
          overscanCount={10}
          // paddingTop={30}
          // paddingBottom={10}
          // padding={25 /* sets both */}
          // renderRow={({
          //   node,
          //   innerRef,
          //   attrs,
          //   children,
          // }: {
          //   node: NodeApi<TreeApiData>;
          //   innerRef: (el: HTMLDivElement | null) => void;
          //   attrs: HTMLAttributes<any>;
          //   children: React.ReactElement;
          // }) => {
          //   console.info();

          //   return children;
          // }}
          renderCursor={({ top, left, indent }: { top: number; left: number; indent: number }) => {
            // Here we could change the cursor styles. See: https://github.com/brimdata/react-arborist?tab=readme-ov-file#cursor-component-props
            return (
              <div
                style={{
                  position: `absolute`,
                  top: `calc(${top - 1}px)`,
                  left: 0,
                  width,
                  height: `2px`,
                  backgroundColor: RoutalPalette.primary20,
                  zIndex: 1000,
                }}
              />
            );
          }}
          renderDragPreview={({ offset, id, isDragging }: DragPreviewProps) => {
            // WARNING: Don't use the mouse prop from DragPreviewProps, for some reason it always stops the drag if trying to acces to it.
            if (id && stopsMap[id] && offset && isDragging) {
              const getStyle = (offset: { x: number; y: number } | null) => {
                if (!offset) return { display: `none` };
                const { x, y } = offset;
                return { transform: `translate(${x}px, ${y}px)` };
              };

              return (
                <div
                  style={{
                    width,
                    height: `100%`,
                    position: `fixed`,
                    zIndex: 100,
                    pointerEvents: `none`,
                    left: 0,
                    top: 0,
                    ...getStyle(offset),
                  }}
                >
                  <PlanOverviewStopChip
                    stop={stopsMap[id]}
                    selectedStops={selectedStops}
                    showDraggingBadge={true}
                    isDragging={true}
                  />
                </div>
              );
            }
            return null;
          }}
          onMove={({ dragIds, dragNodes, parentId, parentNode, index }) => {
            if (parentId && parentNode) {
              const stops = dragNodes.map((node) => stopsMap[node.id]).filter((stop) => stop !== undefined);
              if (parentId === `unassigned`) {
                // Just move to unassigned, it doesn't matter the index or position
                updateDriverStops({
                  stops: stops,
                  index: 0,
                });
                return;
              }

              // Find which of the nodes are inside the destination tree
              const insideTreeNodes = dragNodes.filter((node) => node.parent?.id === parentNode?.id);

              // Calculate the offset that will be substracted to the final index of the dragging nodes inside the destination tree.
              // This is done because the nodes that are being dragged, shouldn't have to be counted if the index is greater or equal to the node's index.
              const selectedOffset = insideTreeNodes.reduce(
                (acc, node) => (index >= (node?.rowIndex ?? 0) ? acc + 1 : acc),
                0
              );

              // Calculate the offset that appears when the reported stops are hidden.
              // This is done because the stops that are hidden should be counted if the index is greater or equal to the hidden stops.
              let completedOffset = 0;
              if (activeProject?.plans.hide_completed_stops) {
                const driver = driversMap[parentId];
                completedOffset = driver.stops.reduce(
                  (acc, stop, stopIndex) =>
                    stop.status !== `pending` && index + acc >= (stopIndex ?? 0) ? acc + 1 : acc,
                  0
                );
              }

              // We calculate the real index of the dragging nodes inside the destination tree.
              const realIndex = index - selectedOffset + completedOffset;

              updateDriverStops({
                driver: driversMap[parentId],
                stops: stops,
                index: realIndex,
              });
            }
          }}
          onSelect={() => {
            if (!treeRef.current) return;

            // If we use the nodes passed by the function, we might be missing nodes because it only passes the visible nodes.
            // While if we access directly to thre tree state, get all the selected nodes properly.
            onSelectStops(Array.from(treeRef.current.state.nodes.selection.ids ?? []));
          }}
          // onActivate={(node: NodeApi<TreeApiData>) => {
          //   console.info(`onActivate`, node);
          // }}
          // onScroll={(props: ListOnScrollProps) => {
          //   console.info(`onScroll`, props);
          // }}
          // onToggle={(id: string) => {
          //   console.info(`onToggle`, id);
          // }}
          // onFocus={(node: NodeApi<TreeApiData>) => {
          //   console.info(`onFocus`, node);
          // }}
        >
          {({ tree, node, style, dragHandle }: NodeRendererProps<TreeApiData>) => {
            const nodeType = node.data.type;
            switch (nodeType) {
              case `plan-no-stops`:
              case `unassigned-no-stops`:
                return (
                  <UnassignedNoStopsElement
                    tree={tree}
                    node={node}
                    style={style}
                    setDialog={(dialog) => {
                      if (dialog === `stopsFromFile`) {
                        intercom.track(INTERCOM_EVENTS.plan.service.upload_file);
                      }
                      setDialog(dialog);
                    }}
                    dragHandle={dragHandle}
                  />
                );
              case `unassigned`:
                return (
                  <UnassignedElement
                    tree={tree}
                    node={node}
                    style={style}
                    selectedStops={selectedStops}
                    dragHandle={dragHandle}
                  />
                );
              case `driver`:
                return (
                  <DriverElement
                    tree={tree}
                    node={node}
                    style={style}
                    selectedStops={selectedStops}
                    dragHandle={dragHandle}
                  />
                );
              case `driver-no-stops`:
                return (
                  <DriverNoStopsElement
                    hideCompleted={hideCompleted}
                    tree={tree}
                    node={node}
                    style={style}
                    dragHandle={dragHandle}
                  />
                );
              case `stop`:
                return (
                  <StopElement
                    selectedStops={selectedStops}
                    tree={tree}
                    node={node}
                    style={style}
                    dragHandle={dragHandle}
                  />
                );
              default:
                return null;
            }
          }}
        </Tree>
      </PlanOverviewListContainer>

      <CreateStopsManuallyDialog
        open={Boolean(dialog === `createStopsManually`)}
        planId={plan.id}
        planStops={stops}
        handleCloseDialog={(e: React.MouseEvent<ButtonProps, MouseEvent>) => {
          e.stopPropagation();
          setDialog(undefined);
        }}
      />

      <DataImporter
        open={Boolean(dialog === `stopsFromFile`)}
        activePlan={plan}
        handleCloseDialog={(e: any) => {
          if (e) e.stopPropagation();
          setDialog(undefined);
        }}
        onComplete={() => {
          setDialog(undefined);
        }}
      />
    </>
  );
};
